'use client';

import Image from 'next/image';
import { useId, useState } from 'react';
import { useTranslations } from 'next-intl';
import { cn } from '@/lib/cn';
import styles from './HomepageStyleStory.module.css';
import { ArrowRight } from 'lucide-react';
import { AnimatedRange } from '@/components/ui/AnimatedBar';

const LEVELS = [0, 25, 50, 75, 100] as const;
const SCENES = ['horse', 'market'] as const;

/** The same five real outputs used in the Style launch film. */
export function HomepageCreativityDemo({ className, onExplore }: { className?: string; onExplore: () => void }) {
  const t = useTranslations('auth.cinematic.creativityDemo');
  const [level, setLevel] = useState(50);
  const id = useId();

  return (
    <section id="creativity-demo" aria-labelledby={`${id}-title`}
      className={cn(styles.chapter, 'scroll-mt-24 text-[#10232E]', className)}>
      <div className="grid items-center gap-8 sm:gap-10 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] lg:gap-12 xl:gap-16">
        <div className="min-w-0">
          <h2 id={`${id}-title`} className={styles.title}>
            {t('title')}
          </h2>
        <div className="mt-8 w-full max-w-[480px] sm:mt-10">
          <AnimatedRange id={id} min={0} max={100} step={25} ticks={5} size="regular"
            value={level} label={t('control')} valueLabel={`${level}%`}
            aria-label={t('control')} aria-valuetext={t('value', { value: level })}
            onChange={event => setLevel(Number(event.target.value))} />
          <div className="mt-1 flex justify-between" aria-label={t('presets')}>
            {LEVELS.map(value => <button key={value} type="button" onClick={() => setLevel(value)}
              aria-pressed={level === value} aria-label={t('value', { value })}
              className={cn('min-h-11 min-w-11 rounded-full text-xs tabular-nums transition-colors hover:text-[#10232E] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4ABDE9]', level === value ? 'font-semibold text-[#10232E]' : 'text-[#77858E]')}>
              {value}%
            </button>)}
          </div>
          <p aria-live="polite" aria-atomic="true" className="mt-1 min-h-6 text-center text-[13px] text-[#6A818B]">{t(`step${level}`)}</p>
        </div>
          <button type="button" className="mt-7 inline-flex min-h-11 items-center gap-2 text-sm font-medium text-[#1686AF]"
            onClick={onExplore}>{t('start')}<ArrowRight size={16} /></button>
        </div>
        <div className="mx-auto grid w-full max-w-[640px] grid-cols-2 gap-3 sm:gap-5 lg:mx-0 lg:justify-self-end">
          {SCENES.map(scene => <figure key={scene} className="min-w-0">
            <div className="relative aspect-[435/635] overflow-hidden rounded-[20px] bg-[#F2F2F2] sm:rounded-[28px]">
              {LEVELS.map(value => <Image key={value} src={scene === 'horse' && value === 100
                ? '/login-gallery/creativity-demo/horse-100-approved.png'
                : `/login-gallery/creativity-demo/${scene}-${value}.jpg`}
                alt={value === level ? t('imageAlt', { scene: t(scene), value }) : ''} aria-hidden={value !== level}
                fill sizes="(max-width: 639px) 45vw, (max-width: 1023px) 40vw, 310px"
                className={cn('object-cover transition-opacity duration-300 motion-reduce:transition-none', value === level ? 'opacity-100' : 'opacity-0')}
              />)}
            </div>
            <figcaption className="mt-3 text-center text-xs font-medium text-[#6A818B] sm:text-sm">{t(scene)}</figcaption>
          </figure>)}
        </div>
      </div>
    </section>
  );
}
